import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useDarkMode } from "../contexts/DarkModeContext";
import Register from "../components/Register";
import Footer from "../components/Footer";

const RegisterPage = () => {
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [progressData, setProgressData] = useState({ current: 1, total: 14 });
  const navigate = useNavigate();

  const handleProgressChange = React.useCallback((current, total) => {
    setProgressData({ current, total });
  }, []);

  const handleHomeNavigation = () => {
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={handleHomeNavigation}
              className="flex items-center space-x-2"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="flex items-center"
              >
                <img
                  src="/buildathon_logo.png"
                  alt="Buildathon"
                  className="h-8 max-[378px]:h-6 w-auto"
                />
              </motion.div>
            </button>
            <div className="flex items-center">
              <Link
                to="/"
                className="text-gray-600 dark:text-gray-300 hover:text-aifund transition-colors duration-200"
              >
                ← Back to Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Applications Closed Message */}
      <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-8 pb-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700 p-8 md:p-12 transition-colors duration-300"
          >
            <div className="mb-8">
              <img
                src="/buildathon_logo.png"
                alt="Buildathon"
                className="h-16 md:h-20 w-auto mx-auto mb-6"
              />
            </div>
            
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Applications Are Now Closed
            </h1>
            
            <div className="text-lg text-gray-700 dark:text-gray-300 leading-relaxed space-y-4 text-left">
              <p>
                Thank you for your interest in participating in the upcoming Buildathon. We were truly impressed by the enthusiasm and energy from so many builders excited to push the boundaries of what's possible with AI.
              </p>
              
              <p>
                Unfortunately, due to limited space and the high volume of strong applications, we're no longer accepting new applications at this time.
              </p>
              
              <p>
                We know this may be disappointing, but we appreciate your interest and would love to keep you in the loop for future opportunities. We're actively exploring ways to expand Buildathon to other locations—and your city may be one of them.
              </p>
              
              <p>
                In the meantime, we invite you to join us virtually via livestream on the day of the event.
              </p>
            </div>
            
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <motion.a
                href="/livestream"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-aifund text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-200 inline-block text-center"
              >
                Join Livestream
              </motion.a>
              
              <motion.a
                href="/"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-aifund text-aifund px-8 py-4 rounded-full font-bold text-lg transition-all duration-200 cursor-pointer inline-block text-center hover:bg-aifund hover:text-white"
              >
                Back to Home
              </motion.a>
            </div>
            
            <div className="mt-8 text-gray-600 dark:text-gray-400">
              <p className="font-medium">Thank you again, and we hope to build with you soon.</p>
              <p className="mt-2 italic">Warmly,<br />The Buildathon Team</p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <Footer />

      {/* Floating Dark Mode Toggle */}
      <motion.button
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={toggleDarkMode}
        className="fixed bottom-4 right-4 z-50 p-3 rounded-full bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-colors duration-200"
        aria-label="Toggle dark mode"
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.3, delay: 1 }}
      >
        {isDarkMode ? (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z"
              clipRule="evenodd"
            />
          </svg>
        ) : (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
          </svg>
        )}
      </motion.button>
    </div>
  );
};

export default RegisterPage;
